import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../../context/AuthContext';
import axios from 'axios';
import {
  Activity,
  Plus,
  RefreshCw,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Edit,
  Shield,
  Info
} from 'lucide-react';
import { Button } from '../../../../components/ui/button';
import { Input } from '../../../../components/ui/input';
import { Badge } from '../../../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../../components/ui/select';
import { Switch } from '../../../../components/ui/switch';
import { cn } from '../../../../lib/utils';
import { toast } from 'sonner';

// Normalize backend URL to prevent double /api paths
const rawBackendUrl = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/$/, '');
const BACKEND_URL = rawBackendUrl.replace(/\/api$/, '');
const API = `${BACKEND_URL}/api`;

const TRACKING_PROVIDERS = [
  { 
    id: 'google_analytics', 
    name: 'Google Analytics', 
    description: 'Track website traffic and user behavior',
    placeholder: 'G-XXXXXXXXXX',
    icon: '📊'
  },
  { 
    id: 'google_tag_manager', 
    name: 'Google Tag Manager', 
    description: 'Manage and deploy marketing tags',
    placeholder: 'GTM-XXXXXX',
    icon: '🏷️'
  },
  { 
    id: 'meta_pixel', 
    name: 'Meta Pixel', 
    description: 'Track conversions for Facebook/Instagram ads',
    placeholder: 'XXXXXXXXXXXXXXXX',
    icon: '📘'
  },
  { 
    id: 'tiktok_pixel', 
    name: 'TikTok Pixel', 
    description: 'Track conversions for TikTok ads',
    placeholder: 'XXXXXXXXXXXX',
    icon: '🎵'
  },
  { 
    id: 'linkedin_insight', 
    name: 'LinkedIn Insight Tag', 
    description: 'Track conversions for LinkedIn ads',
    placeholder: 'XXXXXXX',
    icon: '💼'
  },
];

const PLACEMENT_OPTIONS = [
  { value: 'all', label: 'All Pages' },
  { value: 'admin_only', label: 'Admin Pages Only' },
  { value: 'portal_only', label: 'Portal Pages Only' },
  { value: 'disabled', label: 'Disabled' },
];

const PlatformExternalTrackingPage = () => {
  const { token } = useAuth();
  const [trackingConfigs, setTrackingConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingConfig, setEditingConfig] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    provider: '',
    tracking_id: '',
    enabled: false,
    placement: 'all'
  });

  useEffect(() => {
    fetchData();
  }, [token]);

  const fetchData = async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/platform/settings/data/external-tracking`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTrackingConfigs(res.data.tracking || []);
    } catch (err) {
      console.error('Failed to fetch tracking configs:', err);
      // Don't show error toast - may just be empty
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.provider) {
      toast.error('Provider is required');
      return;
    }

    try {
      if (editingConfig) {
        await axios.put(`${API}/platform/settings/data/external-tracking/${editingConfig.id}`, {
          tracking_id: formData.tracking_id,
          enabled: formData.enabled,
          placement: formData.placement
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Tracking configuration updated');
      } else {
        await axios.post(`${API}/platform/settings/data/external-tracking`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Tracking configuration created');
      }
      setShowAddModal(false);
      setEditingConfig(null);
      resetForm();
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save configuration');
    }
  };

  const resetForm = () => {
    setFormData({
      provider: '',
      tracking_id: '',
      enabled: false,
      placement: 'all'
    });
  };

  const openEditModal = (config) => {
    setEditingConfig(config);
    setFormData({
      provider: config.provider,
      tracking_id: config.tracking_id || '',
      enabled: config.enabled,
      placement: config.placement || 'all'
    });
    setShowAddModal(true);
  };

  const toggleEnabled = async (config) => {
    try {
      await axios.put(`${API}/platform/settings/data/external-tracking/${config.id}`, {
        ...config,
        enabled: !config.enabled
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(config.enabled ? 'Tracking disabled' : 'Tracking enabled');
      fetchData();
    } catch (err) {
      toast.error('Failed to update tracking');
    }
  };

  const getProviderInfo = (providerId) => {
    return TRACKING_PROVIDERS.find(p => p.id === providerId) || { name: providerId, icon: '📈' };
  };

  const configuredProviders = trackingConfigs.map(c => c.provider);
  const availableProviders = TRACKING_PROVIDERS.filter(p => !configuredProviders.includes(p.id));

  return (
    <div className="min-h-screen bg-[#0B1F18] p-8" data-testid="platform-external-tracking-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-500 to-cyan-600 flex items-center justify-center">
            <Activity className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white">External Tracking</h1>
            <p className="text-[#93AE9F] text-sm">
              Configure analytics and tracking pixels for the platform
            </p>
          </div>
        </div>
        {availableProviders.length > 0 && (
          <Button
            onClick={() => {
              resetForm();
              setEditingConfig(null);
              setShowAddModal(true);
            }}
            className="bg-amber-500 hover:bg-amber-600 text-white"
            data-testid="add-tracking-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Tracking
          </Button>
        )}
      </div>

      {/* Security Notice */}
      <div className="mb-6 p-4 bg-amber-500/10 border border-amber-500/20 rounded-xl">
        <div className="flex items-start gap-3">
          <Shield className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="font-semibold text-amber-400 mb-1">Security Notice</h4>
            <p className="text-sm text-amber-300/80">
              Only Platform Owners can configure external tracking. Tracking IDs are sanitized 
              and scripts are loaded from official CDNs only. Custom scripts are not allowed 
              to prevent injection attacks.
            </p>
          </div>
        </div>
      </div>

      {/* Tracking Cards */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="w-6 h-6 text-emerald-500 animate-spin" />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Configured Tracking */}
          {trackingConfigs.map((config) => {
            const provider = getProviderInfo(config.provider);
            return (
              <div
                key={config.id}
                className="bg-[rgba(10,38,28,0.6)] border border-[rgba(142,218,182,0.12)] rounded-xl p-6"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">{provider.icon}</span>
                    <div>
                      <h3 className="font-semibold text-white">{provider.name}</h3>
                      <p className="text-xs text-[#93AE9F]">{provider.description}</p>
                    </div>
                  </div>
                  <Switch
                    checked={config.enabled}
                    onCheckedChange={() => toggleEnabled(config)}
                  />
                </div>
                
                <div className="space-y-3">
                  <div>
                    <label className="text-xs text-[#93AE9F]">Tracking ID</label>
                    <p className="text-white font-mono text-sm">
                      {config.tracking_id || <span className="text-zinc-500 italic">Not configured</span>}
                    </p>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div>
                      <label className="text-xs text-[#93AE9F]">Placement</label>
                      <p className="text-white text-sm capitalize">
                        {config.placement?.replace('_', ' ') || 'All Pages'}
                      </p>
                    </div>
                    <Badge className={cn(
                      'border-0',
                      config.enabled
                        ? 'bg-emerald-500/20 text-emerald-400'
                        : 'bg-zinc-600/50 text-zinc-400'
                    )}>
                      {config.enabled ? (
                        <>
                          <CheckCircle className="w-3 h-3 mr-1" />
                          Active
                        </>
                      ) : (
                        <>
                          <XCircle className="w-3 h-3 mr-1" />
                          Inactive
                        </>
                      )}
                    </Badge>
                  </div>
                </div>
                
                <div className="mt-4 pt-4 border-t border-[rgba(142,218,182,0.12)]">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => openEditModal(config)}
                    className="w-full border-zinc-600 text-zinc-300"
                  >
                    <Edit className="w-4 h-4 mr-2" />
                    Configure
                  </Button>
                </div>
              </div>
            );
          })}
          
          {/* Available Providers */}
          {availableProviders.map((provider) => (
            <div
              key={provider.id}
              className="bg-[rgba(10,38,28,0.3)] border border-dashed border-[rgba(142,218,182,0.2)] rounded-xl p-6 opacity-60 hover:opacity-100 transition-opacity"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <span className="text-2xl">{provider.icon}</span>
                  <div>
                    <h3 className="font-semibold text-white">{provider.name}</h3>
                    <p className="text-xs text-[#93AE9F]">{provider.description}</p>
                  </div>
                </div>
                <Badge className="bg-zinc-700 text-zinc-400 border-0">Not Configured</Badge>
              </div>
              
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setFormData({ ...formData, provider: provider.id });
                  setEditingConfig(null);
                  setShowAddModal(true);
                }}
                className="w-full border-zinc-600 text-zinc-300 mt-4"
              >
                <Plus className="w-4 h-4 mr-2" />
                Set Up
              </Button>
            </div>
          ))}
        </div>
      )}

      {/* Info Box */}
      <div className="mt-6 p-4 bg-blue-500/10 border border-blue-500/20 rounded-xl">
        <div className="flex items-start gap-3">
          <Info className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="font-semibold text-blue-400 mb-1">About External Tracking</h4>
            <p className="text-sm text-blue-300/80">
              External tracking helps you understand user behavior, measure conversions, and 
              optimize your marketing campaigns. Tracking codes are loaded asynchronously 
              to minimize impact on page load times.
            </p>
          </div>
        </div>
      </div>

      {/* Add/Edit Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="bg-zinc-900 border-zinc-700 text-white max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingConfig ? 'Configure Tracking' : 'Add Tracking'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {!editingConfig && (
              <div>
                <label className="block text-sm font-medium text-zinc-400 mb-2">Provider *</label>
                <Select
                  value={formData.provider}
                  onValueChange={(v) => setFormData({ ...formData, provider: v })}
                >
                  <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white">
                    <SelectValue placeholder="Select provider" />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-800 border-zinc-700">
                    {availableProviders.map((provider) => (
                      <SelectItem key={provider.id} value={provider.id}>
                        <span className="flex items-center gap-2">
                          <span>{provider.icon}</span>
                          {provider.name}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            <div>
              <label className="block text-sm font-medium text-zinc-400 mb-2">Tracking ID</label>
              <Input
                value={formData.tracking_id}
                onChange={(e) => setFormData({ ...formData, tracking_id: e.target.value })}
                placeholder={getProviderInfo(formData.provider).placeholder || 'Enter tracking ID'}
                className="bg-zinc-800 border-zinc-700 text-white font-mono"
              />
              <p className="text-xs text-zinc-500 mt-1">
                Find this in your {getProviderInfo(formData.provider).name || 'provider'} dashboard
              </p>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-zinc-400 mb-2">Placement</label>
              <Select
                value={formData.placement}
                onValueChange={(v) => setFormData({ ...formData, placement: v })}
              >
                <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-800 border-zinc-700">
                  {PLACEMENT_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex items-center justify-between pt-2">
              <div>
                <label className="text-sm font-medium text-white">Enabled</label>
                <p className="text-xs text-zinc-500">Start tracking immediately</p>
              </div>
              <Switch
                checked={formData.enabled}
                onCheckedChange={(checked) => setFormData({ ...formData, enabled: checked })}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowAddModal(false);
                setEditingConfig(null);
                resetForm();
              }}
              className="border-zinc-600 text-zinc-300"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              className="bg-amber-500 hover:bg-amber-600 text-white"
            >
              {editingConfig ? 'Save Changes' : 'Add Tracking'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PlatformExternalTrackingPage;
